class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.formId = this.dataset.formId;
    this.mainForm = document.getElementById(this.formId);
    if (!this.mainForm) return;

    this.mainButton = this.mainForm.querySelector('.product-form__submit');
    if (!this.mainButton) return;

    this.stickyButton = this.querySelector('[data-sticky-atc-button]');
    this.stickyButtonText = this.querySelector('[data-sticky-atc-button-text]');
    this.stickySpinner = this.querySelector('[data-sticky-atc-spinner]');
    this.stickyPrice = this.querySelector('[data-sticky-atc-price]');

    this.setupVisibilityObserver();
    this.setupStateMirror();
    this.setupPriceMirror();
    this.setupSubmitLifecycle();
  }

  setupVisibilityObserver() {
    if (!('IntersectionObserver' in window)) {
      this.show();
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) this.hide();
        else this.show();
      },
      { threshold: 0, rootMargin: '0px 0px -10px 0px' }
    );

    this.intersectionObserver.observe(this.mainButton);
  }

  show() {
    this.classList.add('sticky-atc--visible');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('sticky-atc-active');
  }

  hide() {
    this.classList.remove('sticky-atc--visible');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('sticky-atc-active');
  }

  setupStateMirror() {
    if (!this.stickyButton) return;
    this.syncFromMain();

    this.mutationObserver = new MutationObserver(() => this.syncFromMain());
    this.mutationObserver.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  syncFromMain() {
    const mainText = this.mainButton.querySelector('span:not(.hidden):not(.loading__spinner)');
    if (mainText && this.stickyButtonText) {
      const text = mainText.textContent.trim();
      if (text) this.stickyButtonText.textContent = text;
    }

    if (this.mainButton.disabled) {
      this.stickyButton.disabled = true;
      this.stickyButton.setAttribute('aria-disabled', 'true');
    } else {
      this.stickyButton.disabled = false;
      this.stickyButton.removeAttribute('aria-disabled');
    }
  }

  setupPriceMirror() {
    if (!this.stickyPrice) return;
    const productInfo = this.closest('product-info');
    if (!productInfo) return;

    const mainPrice = productInfo.querySelector('.price:not(.price--end)');
    if (!mainPrice) return;

    const sync = () => {
      const regular = mainPrice.querySelector('.price-item--regular');
      const sale = mainPrice.querySelector('.price-item--sale:not(.price-item--last)');
      const compareAt = mainPrice.querySelector('.price__sale .price-item--regular');

      const onSale = mainPrice.classList.contains('price--on-sale');
      if (onSale && sale && compareAt) {
        this.stickyPrice.innerHTML =
          '<span class="sticky-atc__price-sale">' + sale.textContent.trim() + '</span>' +
          '<s class="sticky-atc__price-was">' + compareAt.textContent.trim() + '</s>';
      } else if (regular) {
        this.stickyPrice.innerHTML = '<span>' + regular.textContent.trim() + '</span>';
      }
    };

    sync();
    this.priceObserver = new MutationObserver(sync);
    this.priceObserver.observe(mainPrice, { childList: true, subtree: true, characterData: true });
  }

  setupSubmitLifecycle() {
    if (!this.stickyButton) return;
    this.stickyButton.addEventListener('click', () => {
      this.stickyButton.classList.add('loading');
      this.stickySpinner?.classList.remove('hidden');
    });

    document.addEventListener('cart:updated', () => this.resetLoading());
    document.addEventListener('cart-drawer:open', () => this.resetLoading());
    this.mainForm.addEventListener('submit', () => {
      setTimeout(() => this.resetLoading(), 2500);
    });
  }

  resetLoading() {
    this.stickyButton?.classList.remove('loading');
    this.stickySpinner?.classList.add('hidden');
  }

  disconnectedCallback() {
    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
    this.priceObserver?.disconnect();
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
